import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { loadGoogleMaps, isGoogleMapsConfigured } from "../../utils/googleMaps";
import { color } from "../../styles/theme";

/**
 * Google Places Autocomplete input for the business address field.
 * Lighter than mobile's MapHandler (no interactive pin-drop / radius) — a
 * static business address doesn't need geofence-style refinement, just
 * search-and-pick, reusing the same Maps script/key as the Create Event
 * location picker (src/utils/googleMaps.js).
 *
 * @param {{ value: string, onSelect: (place: {address, city, country, postal_code, latitude, longitude}) => void, placeholder?: string }} props
 */
const AddressAutocomplete = ({ value, onSelect, placeholder }) => {
  const { t } = useTranslation(["settings", "common"]);
  const resolvedPlaceholder = placeholder ?? t("addressAutocomplete.defaultPlaceholder");
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [status, setStatus] = useState(isGoogleMapsConfigured() ? "loading" : "unconfigured");

  useEffect(() => {
    if (!isGoogleMapsConfigured()) return;
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setStatus("ready"); })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (status !== "ready" || !inputRef.current || autocompleteRef.current) return;
    const google = window.google;
    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      fields: ["geometry", "formatted_address", "address_components"],
    });
    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      const loc = place?.geometry?.location;
      if (!loc) return;
      const components = place.address_components || [];
      const find = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
      onSelectRef.current?.({
        address: place.formatted_address || "",
        city: find("locality") || find("postal_town") || "",
        country: find("country") || "",
        postal_code: find("postal_code") || "",
        latitude: loc.lat(),
        longitude: loc.lng(),
      });
    });
  }, [status]);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        defaultValue={value}
        placeholder={status === "unconfigured" ? t("addressAutocomplete.unavailablePlaceholder") : resolvedPlaceholder}
        disabled={status !== "ready"}
        className="input"
      />
      {status === "error" && <p style={s.hint}>{t("addressAutocomplete.loadError")}</p>}
      {status === "unconfigured" && (
        <p style={s.hint}>{t("addressAutocomplete.unconfiguredHint")}</p>
      )}
    </div>
  );
};

const s = {
  hint: { fontSize: 11, color: color.danger500, marginTop: 4 },
};

export default AddressAutocomplete;
